import { Capacitor, CapacitorHttp } from "@capacitor/core";
import type { VideoItem, EpisodeItem } from "./data";
export const IS_DEMO =
  import.meta.env.DEV &&
  typeof location !== "undefined" &&
  new URLSearchParams(location.search).get("demo") === "1";
const WEB_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";
const NATIVE_BASE =
  import.meta.env.VITE_NATIVE_API_BASE_URL || "https://ytapp.bbccys.com/api/v1";
export interface RemoteVideo {
  id: string;
  title?: string;
  name?: string;
  contentType?: string;
  type?: string;
  portrait?: string;
  landscape?: string;
  description?: string;
  actors?: string;
  year?: string;
  area?: string;
  episodeCount?: number;
  creator?: string;
  source?: { collectorName?: string };
  playlist?: string;
  episodes?: {
    id?: string;
    episode?: number;
    title?: string;
    url?: string;
    status?: string;
  }[];
  categories?: string[];
  category?: string;
}
export interface FeedResponse {
  items: RemoteVideo[];
  total: number;
  nextCursor: string | null;
  hasMore: boolean;
}
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}
export function mediaUrl(value?: string) {
  if (!value) return "";
  try {
    const url = new URL(value, "https://ytapp.bbccys.com");
    return url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}
async function getJSON(path: string, signal?: AbortSignal): Promise<unknown> {
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  if (Capacitor.isNativePlatform()) {
    const response = await CapacitorHttp.get({
      url: NATIVE_BASE.replace(/\/$/, "") + path,
      headers: { Accept: "application/json" },
      connectTimeout: 12000,
      readTimeout: 12000,
    });
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    if (response.status < 200 || response.status >= 300)
      throw new ApiError(`接口读取失败（${response.status}）`, response.status);
    return typeof response.data === "string"
      ? JSON.parse(response.data)
      : response.data;
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort);
  const timer = window.setTimeout(abort, 12000);
  try {
    const response = await fetch(WEB_BASE.replace(/\/$/, "") + path, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      credentials: "omit",
    });
    if (!response.ok)
      throw new ApiError(`接口读取失败（${response.status}）`, response.status);
    return await response.json();
  } catch (error) {
    if (signal?.aborted) throw error;
    if (controller.signal.aborted) throw new ApiError("接口请求超时，请重试");
    if (error instanceof ApiError) throw error;
    throw new ApiError("暂时无法连接视频服务，请检查网络");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}
export async function getFeed(
  cursor = "",
  signal?: AbortSignal,
): Promise<FeedResponse> {
  const body = (await getJSON(
    `/content/feed?limit=18${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    signal,
  )) as FeedResponse;
  if (!body || !Array.isArray(body.items) || typeof body.total !== "number")
    throw new ApiError("视频列表格式不正确");
  return {
    ...body,
    items: body.items.filter((v) => v && typeof v.id === "string"),
    nextCursor: typeof body.nextCursor === "string" ? body.nextCursor : null,
    hasMore: !!body.hasMore,
  };
}
export async function getVideo(
  id: string,
  signal?: AbortSignal,
): Promise<RemoteVideo> {
  const body = (await getJSON(
    `/content/videos/${encodeURIComponent(id)}`,
    signal,
  )) as RemoteVideo;
  if (!body || typeof body.id !== "string")
    throw new ApiError("视频详情格式不正确");
  return body;
}
export function adaptVideo(remote: RemoteVideo): VideoItem {
  const cover = mediaUrl(remote.portrait || remote.landscape) || "/icon.svg";
  const episodes: EpisodeItem[] = (
    Array.isArray(remote.episodes) ? remote.episodes : []
  )
    .filter((e) => mediaUrl(e.url))
    .map((e, i) => ({
      id: e.id || `${remote.id}-${i + 1}`,
      number: Number(e.episode) || i + 1,
      title: e.title || `第${i + 1}集`,
      src: mediaUrl(e.url),
      cover,
      duration: "分集",
    }));
  if (!episodes.length && mediaUrl(remote.playlist))
    episodes.push({
      id: `${remote.id}-1`,
      number: 1,
      title: "正片",
      src: mediaUrl(remote.playlist),
      cover,
      duration: "正片",
    });
  return {
    id: remote.id,
    title: remote.title || remote.name || "未命名视频",
    description:
      remote.description && remote.description !== "暂无简介"
        ? remote.description
        : [remote.actors, remote.area, remote.year]
            .filter(Boolean)
            .join(" · ") || "暂无简介",
    creator: remote.creator || remote.source?.collectorName || "樱桃视频",
    category: remote.contentType || remote.type || "其他",
    cover,
    src: episodes[0]?.src,
    likes: 0,
    comments: 0,
    saves: 0,
    collection: remote.title || remote.name || "视频合集",
    episode: episodes[0]?.number || 1,
    duration: `${remote.episodeCount || episodes.length || 1}集`,
    remote: true,
    labels: Array.isArray(remote.categories)
      ? remote.categories
      : typeof remote.category === "string"
        ? remote.category.split(/\s*\/\s*/).filter((c) => c && c !== "未分类")
        : [],
    episodeCount: remote.episodeCount || episodes.length || 1,
    episodes,
    actors: remote.actors,
    detailLoaded: Array.isArray(remote.episodes) || !!remote.playlist,
  };
}
export async function getContentMenu(signal?: AbortSignal) {
  const body = (await getJSON("/content/menu", signal)) as {
    types?: string[];
    labels?: string[];
  };
  if (!Array.isArray(body.types) || !Array.isArray(body.labels))
    throw new ApiError("分类配置格式不正确");
  return {
    types: body.types.filter((v) => typeof v === "string"),
    labels: body.labels.filter((v) => typeof v === "string"),
  };
}
